import type { CategoryDTO } from 'ecosystem'

/**
 * Recursively filters and sorts a list of categories.
 *
 * - Removes null or undefined input by returning an empty array.
 * - Sorts categories by their `order` property.
 * - Recursively applies the same filtering and sorting to subcategories.
 *
 * @param categories - The list of categories to filter and sort.
 * @returns A new list of categories that are sorted and have recursively filtered subcategories.
 */
export const filterCategories = (categories?: CategoryDTO[] | null): CategoryDTO[] => {
  if (!categories) {
    return []
  }

  return sortCategories(categories).map((category) => {
    return {
      ...category,
      ...(category.subCategories?.length
        ? {
            subCategories: filterCategories(category.subCategories)
          }
        : {})
    }
  })
}

/**
 * Filters and sorts categories by the `order` field.
 *
 * - Excludes categories without an `order` value.
 * - Sorts remaining categories in ascending order based on the `order` property.
 *
 * @param categories - The list of categories to sort.
 * @returns A sorted list of categories with a defined `order`.
 */
export const sortCategories = (categories: CategoryDTO[]) =>
  categories.filter((c) => c.order).sort((a, b) => a.order - b.order)

/**
 * Flattens a nested category structure into a single-level array.
 *
 * - Traverses the entire category tree and collects all categories into a flat array.
 * - Includes the parent and all subcategories in the resulting list.
 *
 * @param categories - The nested list of categories to flatten.
 * @returns A flat array containing all categories and subcategories.
 */
export const flattenCategories = (categories?: CategoryDTO[] | null) => {
  const res: CategoryDTO[] = []

  const flatFn = (_categories?: CategoryDTO[] | null) => {
    if (!_categories) {
      return
    }

    _categories.forEach((category) => {
      res.push(category)

      flatFn(category.subCategories)
    })
  }

  flatFn(categories)

  return res
}
